'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal/modal-store';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { AnalyticsCategory, trackEvent } from '@nx-frontend/shared/analytics';
import {
  ActivityGroup,
  NotificationActivityEnrichedRecord,
  User,
  WorkspaceServiceWorkspaceDto,
  PopupType,
  MenuItem,
  NotificationsPortalContext,
} from '@nx-frontend/shared/types';
import { ContextMenuItemType, UiContextMenuConfigPosition } from '@nx-frontend/shared/types';
import { ActivityItemGroup } from './activity-item-group';
import { NotificationPanelFilter } from './notification-panel-filter';
import { NotificationPanelFilterWorkspace } from './notification-panel-filter-workspace-icon';
import {
  $activitiesStore,
  $notificationPanelFilter,
  NotificationTabs,
  fetchMoreActivitiesEvent,
  markAllActivityAsReadEvent,
  setNotificationPanelCurrentWorkspaceIdEvent,
  setNotificationPanelTabEvent,
  setNotificationPanelVisibilityEvent,
  setShowUnreadEvent,
  setShowNotificationPanelFilterEvent,
  $isNotificationPanelFilterActive,
  setNotificationPanelContextEvent,
} from './store';

export function NotificationPanel({
  sessionId,
  currentWorkspaceId,
  workspaces = [],
  user,
  assetsPrefixUrl,
  gatewayUrl,
  fetchChatActivities = true,
  portalContext,
  members = [],
  workspace,
  showFilter,
  routerPush,
  openCommentsByThreadId,
  selectMenuItem,
  getPagePortalMenuItem,
  showFileInDashboard,
  viewMode = 'popup',
  trackEventCategory,
  trackEventLabelPrefix,
}: {
  sessionId: string;
  currentWorkspaceId: string;
  workspaces?: WorkspaceServiceWorkspaceDto[];
  user: User;
  assetsPrefixUrl: string;
  gatewayUrl: string;
  fetchChatActivities?: boolean;
  portalContext: NotificationsPortalContext;
  members?: User[];
  workspace?: NotificationPanelFilterWorkspace;
  showFilter?: boolean;
  routerPush: (url: string) => void;
  openCommentsByThreadId?: (threadId: string, commentId: string) => void;
  selectMenuItem?: (itemId: string) => void;
  getPagePortalMenuItem?: (params: {
    globalId?: string;
    portalPageId?: string;
  }) => (MenuItem & { parentItem?: MenuItem | null; portalDomain?: string; portalId?: string }) | null;
  showFileInDashboard?: (globalId: string) => void;
  viewMode?: 'popup' | 'block';
  trackEventCategory?: AnalyticsCategory;
  trackEventLabelPrefix?: string;
}) {
  const openContextMenu = useEvent(openContextMenuEvent);
  const notificationRef = useRef<HTMLDivElement>(null);

  const [activitiesStore, notificationPanelFilter, isNotificationPanelFilterActive] = useUnit([
    $activitiesStore,
    $notificationPanelFilter,
    $isNotificationPanelFilterActive,
  ]);

  const [
    fetchMoreActivities,
    markAllActivityAsRead,
    setShowUnread,
    openModal,
    closeModal,
    setNotificationPanelVisibility,
    setNotificationPanelTab,
    setNotificationPanelCurrentWorkspaceId,
    setShowNotificationPanelFilter,
    setNotificationPanelContext,
  ] = useUnit([
    fetchMoreActivitiesEvent,
    markAllActivityAsReadEvent,
    setShowUnreadEvent,
    openModalEvent,
    closeModalEvent,
    setNotificationPanelVisibilityEvent,
    setNotificationPanelTabEvent,
    setNotificationPanelCurrentWorkspaceIdEvent,
    setShowNotificationPanelFilterEvent,
    setNotificationPanelContextEvent,
  ]);
  const t = useTranslations('notification_panel');
  const lazyLoadingMark = useRef<HTMLDivElement>(null);
  const activities = activitiesStore;

  const activitiesTabs = [
    { id: 'important', label: t('notification_panel-important') },
    { id: 'updates', label: t('notification_panel-other') },
  ];

  const emptyState = useMemo(
    () => (
      <div className="flex flex-col items-center gap-5 pt-[60px] pb-[131px]">
        <div className="flex items-center justify-center w-[166px] h-[146px] shrink-0 rounded-[10px] border-[0.6px] border-dashed border-neutral-200 bg-neutral-100">
          <Icon icon={IconsMap.NOTIFICATION_ZERO_STATE} className="w-[52px] h-[52px] text-neutral-800" />
        </div>
        <div className="flex flex-col gap-[6px] items-center ">
          <div className="text-neutral-800 text-[16px] font-bold  leading-[19.52px]">
            {t('notification_panel-no_updates_found')}
          </div>
          <div className="text-neutral-800 text-[12px] leading-[15.6px] ">{t('notification_panel-we_notify_you')}</div>
        </div>
      </div>
    ),
    [t]
  );

  useEffect(() => {
    setNotificationPanelCurrentWorkspaceId(currentWorkspaceId);
    setNotificationPanelContext({
      gatewayUrl,
      sessionId,
      userId: user.id,
      fetchChatActivities,
      portalContext,
    });
  }, [
    currentWorkspaceId,
    gatewayUrl,
    sessionId,
    user.id,
    fetchChatActivities,
    portalContext,
    setNotificationPanelCurrentWorkspaceId,
    setNotificationPanelContext,
  ]);

  const activeTab = useMemo(() => {
    return activitiesStore.workspaces[currentWorkspaceId]?.tab ?? 'important';
  }, [activitiesStore, currentWorkspaceId]);

  const showUnread = useMemo(() => {
    return activitiesStore.workspaces[currentWorkspaceId]?.showUnread ?? false;
  }, [activitiesStore, currentWorkspaceId]);

  const handleTabClick = (tab: NotificationTabs) => {
    setNotificationPanelTab(tab);
  };

  const handleFilterBtnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setShowNotificationPanelFilter(!notificationPanelFilter.show);
      if (trackEventCategory && trackEventLabelPrefix) {
        trackEvent('click', trackEventCategory, `${trackEventLabelPrefix}_view_updates`);
      }
    },
    [notificationPanelFilter.show, setShowNotificationPanelFilter, trackEventCategory, trackEventLabelPrefix]
  );

  const getActionPeriod = useCallback((timestamp: number) => {
    const today = new Date();
    const actionDate = new Date(timestamp * 1000);

    if (
      actionDate.getDate() === today.getDate() &&
      actionDate.getMonth() === today.getMonth() &&
      actionDate.getFullYear() === today.getFullYear()
    ) {
      return 'today';
    } else {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (
        actionDate.getDate() === yesterday.getDate() &&
        actionDate.getMonth() === yesterday.getMonth() &&
        actionDate.getFullYear() === yesterday.getFullYear()
      ) {
        return 'yesterday';
      } else {
        return 'earlier';
      }
    }
  }, []);

  const getCalculatedActionTime = useCallback(
    (time: number) => {
      const currentTime = new Date().getTime();
      const eventTime = new Date(time * 1000).getTime();

      const timeDifference = currentTime - eventTime;
      const secondsDifference = Math.floor(timeDifference / 1000);
      const minutesDifference = Math.floor(secondsDifference / 60);
      const hoursDifference = Math.floor(minutesDifference / 60);

      if (minutesDifference < 1) {
        return t('notification_panel-just_now');
      } else if (hoursDifference < 1) {
        return t('notification_panel-min_ago', { minutesDifference });
      } else {
        const eventDate = new Date(time * 1000);
        const currentDate = new Date();

        if (
          eventDate.getDate() === currentDate.getDate() &&
          eventDate.getMonth() === currentDate.getMonth() &&
          eventDate.getFullYear() === currentDate.getFullYear()
        ) {
          return eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (
          eventDate.getDate() === currentDate.getDate() - 1 &&
          eventDate.getMonth() === currentDate.getMonth() &&
          eventDate.getFullYear() === currentDate.getFullYear()
        ) {
          return t('notification_panel-yesterday');
        } else {
          return eventDate.toLocaleDateString();
        }
      }
    },
    [t]
  );

  const { notificationItemsImportant, notificationItemsOther } = useMemo(() => {
    const categorizeActivities = (activities: NotificationActivityEnrichedRecord[]) => {
      const todayItems = activities.filter((entity) => entity.actionPeriod === 'today');
      const yesterdayItems = activities.filter((entity) => entity.actionPeriod === 'yesterday');
      const earlierItems = activities.filter((entity) => entity.actionPeriod === 'earlier');
      return { today: todayItems, yesterday: yesterdayItems, earlier: earlierItems };
    };

    const customizeActivity = (activity: NotificationActivityEnrichedRecord) => {
      if (!activities) return;

      const mode = portalContext?.mode || 'webnotes';
      // For webnotes client activities
      if (mode === 'webnotes') {
        const matchingWorkspace = workspaces.find((workspace) => workspace.globalId === activity.workspaceId);

        activity.actionPlace = t('notification_panel-internal_space');
        activity.actionPlaceUrl = `/space/${activity.workspaceId}/all`;
        if (activity.note) {
          activity.type = activity.note.type;
          if (activity.note.isSharedForPortal) {
            // const workspacePortal = orgPortalSettings.find(
            //   (portal) => portal.settings.workspaceId === activity.workspaceId
            // );
            // const { name, domain } = workspacePortal?.settings ?? {};
            activity.actionPlace = portalContext?.settings.name;
            activity.actionPlaceUrl = `https://${portalContext?.settings.domain}`;
          }
        }
        if (matchingWorkspace) {
          activity.workspaceTitle = matchingWorkspace.title;
        }

        const parentId = activity.extra.noteParentId;

        if (parentId !== 'trash') {
          if (parentId === 'root' || parentId === 'default') {
            activity.parentTitle = 'noParent';
            if (activity.noteParent) {
              activity.parentTitle = activity.noteParent.title;
            }
          } else if (activity.noteParent) {
            activity.parentTitle = activity.noteParent.title;
            if (activity.noteParent.parentId === 'trash' || activity.isParentDeleted) {
              activity.parentInTrash = true;
            }
          }
        }
      } else {
        // For portal activities
        const { name } = portalContext.settings;
        const { portalId } = portalContext;
        activity.actionPlace = name;
        activity.actionPlaceUrl = '/';

        const breadcrumbs = activity.extra?.breadcrumbs;
        // breadcrumbs for portal page
        if (breadcrumbs) {
          activity.portalPage = { pageId: '', visible: true, noteIds: [], portalId, breadcrumbs };
        }
      }

      // For portal page get parent folder in portal
      if (activity.note?.isSharedForPortal && getPagePortalMenuItem) {
        const portalMenuItem = getPagePortalMenuItem({
          globalId: activity.note.globalId,
          portalPageId: activity.extra?.portalPageId,
        });
        if (portalMenuItem) {
          const { parentItem, portalDomain } = portalMenuItem;
          if (parentItem) {
            activity.parentTitle = parentItem.name;
            if (parentItem.url && portalDomain) {
              if (mode === 'webnotes') {
                activity.parentUrl = `https://${portalDomain}${parentItem.url}`;
              }
              if (mode === 'portals') {
                activity.parentUrl = parentItem.url;
              }
            }
          } else {
            // TODO: replace noParent to be empty string
            activity.parentTitle = 'noParent';
          }
          activity.portalPageUrl = portalMenuItem.url;
          activity.portalPageName = portalMenuItem.name;
          // check if on portal page there are more than one notes — title will from kitchen
          const notesInPortalPage = activity.extra?.portalPageNoteIds?.length ?? 1;
          if (notesInPortalPage > 1) {
            activity.portalPageName = activity.extra.noteTitle ?? '';
          }
          if (mode === 'webnotes') {
            activity.portalPageUrl = `https://${portalDomain}${portalMenuItem.url}`;
          }
        } else {
          // If portal page not found, it's mean page in widget
          // TODO: replace noParent to be empty string
          activity.parentTitle = 'noParent';
        }
      }

      activity.actionPeriod = getActionPeriod(activity.date);
      activity.actionTime = getCalculatedActionTime(activity.date);
    };

    const filteredActivities = Object.values(activitiesStore.workspaces[currentWorkspaceId]?.entities ?? {}).filter(
      (entity) => entity.userId !== 0
    );

    filteredActivities.forEach((activity) => {
      customizeActivity(activity);
    });

    const importantActivities: NotificationActivityEnrichedRecord[] = [];
    const updatesActivities: NotificationActivityEnrichedRecord[] = [];

    filteredActivities.forEach((activity) => {
      const { objectType, action, comment } = activity;
      if (
        (objectType === 'comment' && (comment?.mentioned || comment?.replyTo)) ||
        (action === 'mention' && !['note', 'folder', 'workspace'].includes(objectType)) ||
        objectType === 'chat'
      ) {
        importantActivities.push(activity);
      } else {
        updatesActivities.push(activity);
      }
    });

    const importantGroups = categorizeActivities(
      importantActivities.filter((activity) => {
        if (showUnread) return !activity.userActivityStreamItemRead;
        return true;
      })
    );

    const updatesGroups = categorizeActivities(updatesActivities);

    return {
      notificationItemsImportant: importantGroups,
      notificationItemsOther: updatesGroups,
    };
  }, [
    activitiesStore,
    activities,
    currentWorkspaceId,
    workspaces,
    showUnread,
    portalContext,
    getActionPeriod,
    getCalculatedActionTime,
    getPagePortalMenuItem,
    t,
  ]);

  // Fetch activities handler
  const handleLoadActivity = useCallback(() => {
    const minUserActivityStreamItemId =
      activitiesStore.workspaces[currentWorkspaceId]?.tabs[activeTab]?.minUserActivityStreamItemId;
    fetchMoreActivities({
      workspaceId: currentWorkspaceId,
      sessionId,
      tab: activeTab,
      hostUrl: gatewayUrl,
      userId: user.id,
      beforeId: minUserActivityStreamItemId,
      fetchChatActivities,
      portalContext,
    });
  }, [
    currentWorkspaceId,
    sessionId,
    activeTab,
    gatewayUrl,
    fetchMoreActivities,
    user,
    activitiesStore,
    fetchChatActivities,
    portalContext,
  ]);

  const handleReadChange = useCallback(() => {
    setShowUnread({
      hostUrl: gatewayUrl,
      workspaceId: currentWorkspaceId,
      sessionId,
      showUnread: !showUnread,
      userId: user.id,
      fetchChatActivities,
      portalContext,
    });
  }, [
    showUnread,
    currentWorkspaceId,
    sessionId,
    user.id,
    gatewayUrl,
    fetchChatActivities,
    portalContext,
    setShowUnread,
  ]);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    const startObserving = () => {
      if (lazyLoadingMark.current) {
        observer = new IntersectionObserver(
          (entites: IntersectionObserverEntry[]) => {
            const [mark] = entites;
            if (mark.isIntersecting) {
              // once mark is visible on page and data is not fetching or loaded, load more data
              handleLoadActivity();
            }
          },
          { root: null, rootMargin: '0px', threshold: 0.3 }
        );
        observer.observe(lazyLoadingMark.current);
      }
    };

    startObserving();

    return () => {
      observer?.disconnect();
    };
  }, [sessionId, currentWorkspaceId, activitiesStore.isFetching, activitiesStore.isLoaded, handleLoadActivity]);

  const { isLoading, tabState } = useMemo(() => {
    const workspaceState = activitiesStore?.workspaces[currentWorkspaceId] || { tabs: {} };
    const tabState = workspaceState.tabs[activeTab] || { isFetching: false, isLoaded: false };
    return {
      isLoading: tabState.isFetching && !tabState.isLoaded,
      tabState,
    };
  }, [activeTab, activitiesStore, currentWorkspaceId]);

  const fetchingSpinner = useMemo(() => {
    return (
      <div ref={lazyLoadingMark} className="min-h-1">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-[52px] my-1">
            <Spinner />
          </div>
        ) : null}
      </div>
    );
  }, [isLoading]);

  const renderActivityGroup = useMemo(
    () => (group: ActivityGroup, category: string) => {
      const { today, yesterday, earlier } = group;
      const totalActivities = today.length + yesterday.length + earlier.length;

      const renderActivityItemGroup = (
        items: NotificationActivityEnrichedRecord[],
        title: string,
        activitiesCategory: string
      ) => {
        if (items.length > 0) {
          return (
            <ActivityItemGroup
              title={title}
              items={items}
              assetsPrefixUrl={assetsPrefixUrl}
              activitiesCategory={activitiesCategory}
              gatewayUrl={gatewayUrl}
              sessionId={sessionId}
              userId={user.id}
              onClose={() => setNotificationPanelVisibility(false)}
              openCommentsByThreadId={openCommentsByThreadId}
              selectMenuItem={selectMenuItem}
              fetchChatActivities={fetchChatActivities}
              portalContext={portalContext}
              routerPush={routerPush}
              mode={portalContext?.mode}
              showFileInDashboard={showFileInDashboard}
            />
          );
        } else {
          return null;
        }
      };

      return (
        <>
          {renderActivityItemGroup(today, t('notification_panel-today'), category)}
          {renderActivityItemGroup(yesterday, t('notification_panel-yesterday'), category)}
          {renderActivityItemGroup(earlier, t('notification_panel-earlier'), category)}
          {tabState.isLoaded && totalActivities === 0 ? emptyState : null}
        </>
      );
    },
    [
      emptyState,
      tabState,
      assetsPrefixUrl,
      gatewayUrl,
      sessionId,
      user.id,
      fetchChatActivities,
      portalContext,
      setNotificationPanelVisibility,
      openCommentsByThreadId,
      selectMenuItem,
      routerPush,
      showFileInDashboard,
      t,
    ]
  );
  //
  const isEmpty = useMemo(() => {
    const tabs: Record<string, boolean> = {
      important:
        notificationItemsImportant.earlier.length +
          notificationItemsImportant.today.length +
          notificationItemsImportant.yesterday.length ===
        0,
      updates:
        notificationItemsOther.earlier.length +
          notificationItemsOther.today.length +
          notificationItemsOther.yesterday.length ===
        0,
    };

    return tabs[activeTab];
  }, [notificationItemsImportant, notificationItemsOther, activeTab]);

  const handleMoreClick = useCallback(
    (e: React.MouseEvent) => {
      const position = calculateContextMenuPosition(
        e.target as HTMLElement,
        0,
        UiContextMenuConfigPosition.BOTTOM_LEFT
      );

      openContextMenu({
        items: [
          {
            type: ContextMenuItemType.DEFAULT,
            title: t('notification_panel-mark_all_as_read'),
            icon: <Icon icon={IconsMap.CHECK_CIRCLE} className="w-[16px] h-[16px] text-black opacity-[0.54]" />,
            disabled: activeTab === 'updates',
            tooltip: activeTab === 'updates' ? t('notification_panel-only_for_important') : undefined,
            action: () => {
              openModal({
                title: t('notification_panel-mark_all_as_read'),
                content: PopupType.DIALOG,
                showCloseButton: true,
                closeButtonSize: 18,
                zIndex: portalContext?.mode === 'portals' ? 1001 : undefined,
                payload: {
                  payloadType: PopupType.DIALOG,
                  text: t('notification_panel-you_sure_mark_all'),
                  buttons: [
                    {
                      text: t('notification_panel-cancel'),
                    },
                    {
                      text: t('notification_panel-yes_mark'),
                      main: true,
                      onClick: () => {
                        markAllActivityAsRead({
                          hostUrl: gatewayUrl,
                          workspaceId: currentWorkspaceId,
                          sessionId,
                          userId: user.id,
                          fetchChatActivities,
                          portalContext,
                        });
                        closeModal();
                      },
                    },
                  ],
                },
              });
            },
          },
        ],
        position: {
          ...position,
          x: position.x - 20,
        },
        zIndex: portalContext?.mode === 'portals' ? 1001 : undefined,
      });
    },
    [
      gatewayUrl,
      currentWorkspaceId,
      sessionId,
      user.id,
      activeTab,
      fetchChatActivities,
      portalContext,
      openContextMenu,
      closeModal,
      openModal,
      markAllActivityAsRead,
      t,
    ]
  );

  const onCloseNotificationPanel = () => {
    setNotificationPanelVisibility(false);
  };

  return (
    <div
      data-cy="notification-panel"
      className={classNames('flex w-full h-full flex-col items-start bg-white', {
        'lg:w-[480px] lg:h-screen absolute top-0 rounded-r-[4px] shadow-[0_2px_15px_0_rgba(163,168,173,0.22)]':
          viewMode === 'popup',
        'rounded-lg': viewMode === 'block',
      })}
      ref={notificationRef}
    >
      <div className="flex w-full p-5 justify-between items-center">
        <div className="flex lg:hidden items-start">
          <button
            data-cy="notif-panel-close"
            className="flex w-[30px] h-[30px] p-[8px] justify-center items-center rounded transition-colors duration-200 hover:bg-neutral-100"
            onClick={onCloseNotificationPanel}
          >
            <Icon icon={IconsMap.ARROW_2_LEFT} className="w-[20px] h-[20px] text-neutral-500 flex-shrink-0" />
          </button>
        </div>
        <div className="flex items-center gap-4 relative z-20">
          <div className="text-neutral-800 text-lg font-semibold leading-6">
            {t('notification_panel-updates_title')}
          </div>
          {showFilter ? (
            <div className="relative">
              <button
                data-cy="notif-panel-filter"
                className="notification-panel__filter-btn flex w-[30px] h-[30px] p-[8px] justify-center items-center rounded transition-colors duration-200 hover:bg-neutral-100 border border-neutral-200"
                onClick={(e) => handleFilterBtnClick(e)}
              >
                <Icon
                  icon={IconsMap.FILTER}
                  className={classNames(
                    'w-[20px] h-[20px]  flex-shrink-0 transition-colors duration-200',
                    isNotificationPanelFilterActive ? 'text-teal-500' : 'text-neutral-500'
                  )}
                />
              </button>
              <NotificationPanelFilter portalContext={portalContext} members={members} workspace={workspace} />
            </div>
          ) : null}
        </div>
        <div className="flex items-start gap-[8px]">
          <button
            data-cy="notif-panel-settings"
            onClick={handleMoreClick}
            className="flex w-[30px] h-[30px] p-[8px] justify-center items-center rounded transition-colors duration-200 hover:bg-neutral-100"
          >
            <Icon icon={IconsMap.MORE_HORIZONTAL} className="w-[20px] h-[20px] text-neutral-500 flex-shrink-0" />
          </button>
          {viewMode === 'popup' && (
            <button
              data-cy="notif-panel-close"
              className="hidden lg:flex w-[30px] h-[30px] p-[8px] justify-center items-center rounded transition-colors duration-200 hover:bg-neutral-100"
              onClick={onCloseNotificationPanel}
            >
              <Icon icon={IconsMap.CLOSE} className="w-[20px] h-[20px] text-neutral-500 flex-shrink-0" />
            </button>
          )}
        </div>
      </div>

      <div className="flex w-full h-[40px] pt-1 px-5 items-center border-b border-b-solid border-b-neutral-200 ">
        <div className="flex gap-[24px] items-start h-[34px] ">
          {activitiesTabs.map((tab) => (
            <div
              data-cy={`notif-panel-${tab.label.toLowerCase()}-tab`}
              key={tab.id}
              className={classNames('h-[35px] flex cursor-pointer', {
                'border-b border-b-solid border-neutral-800': activeTab === tab.id,
              })}
              onClick={() => handleTabClick(tab.id as NotificationTabs)}
            >
              <div
                className={classNames('pt-[4px] text-[14px] text-neutral-500', {
                  'font-bold text-neutral-800': activeTab === tab.id,
                })}
              >
                {tab.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {activeTab === 'important' && tabState.isFirstLoad === false && !tabState.isFirstLoadEmpty ? (
        <div className="flex w-full py-3 px-5  justify-end">
          <div className="flex gap-2 ">
            <div className="text-neutral-800 text-[12px] "> {t('notification_panel-only_show_unread')}</div>
            <Checkbox
              id="index"
              checked={showUnread}
              type={CheckboxType.SWITCH}
              boxSize={CheckboxSize.SM}
              onChange={handleReadChange}
            />
          </div>
        </div>
      ) : null}

      <div
        className={classNames(
          'w-full grow overflow-y-auto',
          isEmpty || (isEmpty && isLoading) ? 'flex flex-col items-center justify-center' : ''
        )}
      >
        {activities
          ? activeTab === 'important'
            ? renderActivityGroup(notificationItemsImportant, 'important')
            : renderActivityGroup(notificationItemsOther, 'updates')
          : null}
        {fetchingSpinner}
      </div>
    </div>
  );
}
